import React, { useRef, useState } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { HiExternalLink, HiArrowRight } from 'react-icons/hi';
import { FaGithub } from 'react-icons/fa';

const projects = [
  {
    title: 'Spend Insight',
    category: 'Full Stack',
    description:
      'Smart expense tracking and budget management application with real-time data visualization, category breakdown, and intelligent budget alerts.',
    tags: ['React', 'Redux', 'Node.js', 'MongoDB'],
    liveUrl: 'https://spend-insight.netlify.app/',
    repoUrl: 'https://github.com/atmanad',
    featured: true,
    accent: '#00f2ff',
  },
  {
    title: 'Tweet App',
    category: 'Full Stack',
    description:
      'Social media application with posting, viewing, liking, and replying to tweets. Deployed with Docker on Azure.',
    tags: ['React', 'Redux', 'SQL', 'Docker', 'Azure'],
    liveUrl: 'https://tweetapp-atman.netlify.app/',
    repoUrl: 'https://github.com/atmanad',
    featured: false,
    accent: '#61DAFB',
  },
  {
    title: 'My Doc',
    category: 'Full Stack',
    description:
      'Healthcare platform with appointment scheduling, chatbot, and e-commerce features for COVID-19 information.',
    tags: ['MERN Stack', 'SendGrid', 'Heroku'],
    liveUrl: 'https://ad-mydoc.netlify.app/',
    repoUrl: 'https://github.com/atmanad',
    featured: false,
    accent: '#6fe068',
  },
  {
    title: 'Mysuru Tourism',
    category: 'AI',
    description:
      'Tourism platform with real-time weather integration, DialogFlow chatbot, and intelligent travel recommendations.',
    tags: ['MERN', 'Flask', 'DialogFlow', 'APIs'],
    liveUrl: 'https://tourism-mysuru.netlify.app/',
    repoUrl: 'https://github.com/atmanad',
    featured: false,
    accent: '#f0abfc',
  },
  {
    title: 'DSC CVRGU',
    category: 'Frontend',
    description:
      'Official website for Google Developer Student Club at C V Raman Global University.',
    tags: ['React', 'Bootstrap', 'Firebase'],
    liveUrl: 'https://dsc-cvrgu.netlify.app/',
    repoUrl: 'https://github.com/atmanad',
    featured: false,
    accent: '#febc2e',
  },
  {
    title: 'Virtual Police Station',
    category: 'Full Stack',
    description:
      'Online platform for complaint registration, NOC applications, and virtual assistance for citizens.',
    tags: ['React', 'Node.js', 'SendGrid', 'Firebase'],
    liveUrl: 'https://client-vps.netlify.app/',
    repoUrl: 'https://github.com/atmanad',
    featured: false,
    accent: '#60a5fa',
  },
];


const categoryIcons = {
  'Full Stack': '⬡',
  'Frontend': '◈',
  'AI': '◉',
};

function ProjectCard({ project, index, isFeatured = false }) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.article
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.5, delay: (index % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className={`group relative flex flex-col justify-between rounded-2xl p-6 sm:p-8 backdrop-blur-md transition-all duration-300 ${
        isFeatured ? 'md:col-span-2' : ''
      }`}
      style={{
        background: isHovered ? 'rgba(18, 26, 36, 0.95)' : 'rgba(13, 20, 29, 0.8)',
        border: `1px solid ${isHovered ? `${project.accent}40` : 'rgba(255,255,255,0.08)'}`,
        boxShadow: isHovered
          ? `0 20px 50px rgba(0,0,0,0.5), 0 0 25px ${project.accent}15`
          : '0 4px 20px rgba(0,0,0,0.2)',
      }}
    >
      {/* Top Gradient Accent line */}
      <div
        className="absolute top-0 left-0 right-0 h-[2px] transition-opacity duration-300"
        style={{
          background: `linear-gradient(90deg, transparent, ${project.accent}, transparent)`,
          opacity: isHovered ? 1 : 0,
        }}
      />

      {/* Radial corner glow on hover */}
      <div
        className="absolute -top-10 -right-10 w-32 h-32 rounded-full pointer-events-none transition-opacity duration-500"
        style={{
          background: `radial-gradient(circle, ${project.accent}18 0%, transparent 70%)`,
          opacity: isHovered ? 1 : 0,
        }}
      />

      <div>
        {/* Header meta info */}
        <div className="flex items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-2">
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-mono font-semibold tracking-wider uppercase"
              style={{
                background: `${project.accent}15`,
                border: `1px solid ${project.accent}35`,
                color: project.accent,
              }}
            >
              <span>{categoryIcons[project.category] || '◆'}</span>
              {project.category}
            </span>
            {isFeatured && (
              <span className="hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-widest text-cyan-400 bg-cyan-950/40 border border-cyan-800/40">
                Featured
              </span>
            )}
          </div>
          <span className="font-mono text-xs text-slate-500 tracking-widest">
            {String(index + 1).padStart(2, '0')}
          </span>
        </div>

        {/* Title */}
        <h3 className="text-xl sm:text-2xl font-bold font-sora text-slate-100 group-hover:text-white transition-colors duration-200 mb-3 tracking-tight">
          {project.title}
        </h3>

        {/* Description */}
        <p className="text-slate-400 text-sm leading-relaxed mb-6">
          {project.description}
        </p>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5 mb-6">
          {project.tags.map((t) => (
            <span
              key={t}
              className="px-2.5 py-1 rounded-md text-[11px] font-mono text-slate-400 bg-white/[0.04] border border-white/[0.08]"
            >
              {t}
            </span>
          ))}
        </div>
      </div>

      {/* Bottom CTA / Links */}
      <div className="pt-4 border-t border-white/[0.08] flex items-center justify-between gap-3">
        <a
          href={project.liveUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold font-sora transition-all duration-200"
          style={{
            background: isHovered ? project.accent : 'rgba(255,255,255,0.06)',
            color: isHovered ? '#080f17' : '#dce3f0',
            border: `1px solid ${isHovered ? project.accent : 'rgba(255,255,255,0.1)'}`,
          }}
        >
          <span>Live Demo</span>
          <HiExternalLink className="text-sm" />
        </a>

        <a
          href={project.repoUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs font-semibold text-slate-400 hover:text-slate-200 border border-white/[0.08] hover:border-white/[0.2] transition-all duration-200"
        >
          <FaGithub className="text-sm" />
          <span>Source</span>
        </a>
      </div>
    </motion.article>
  );
}

export default function Projects() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const [activeFilter] = useState('All');

  const filtered = activeFilter === 'All'
    ? projects
    : projects.filter((p) => p.category === activeFilter);

  return (
    <section id="projects" className="section-padding relative overflow-hidden">
      {/* Divider line top */}
      <div className="absolute top-0 left-[10%] right-[10%] h-[1px] bg-gradient-to-r from-transparent via-cyan-500/20 to-transparent" />

      <div className="container-custom" ref={ref}>
        {/* Section Header */}
        <div className="mb-12 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5 }}
              className="section-label mb-4"
            >
              05 / PROJECTS
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="heading-primary"
            >
              Selected <span className="text-gradient">Work</span>
            </motion.h2>
          </div>
        </div>

        {/* Project Grid */}
        <AnimatePresence mode="wait">
          {filtered.length > 0 ? (
            <motion.div
              key={activeFilter}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {filtered.map((project, i) => (
                <ProjectCard
                  key={project.title}
                  project={project}
                  index={i}
                  isFeatured={project.featured && activeFilter === 'All'}
                />
              ))}
            </motion.div>
          ) : (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-center py-20 text-slate-400"
            >
              <span className="font-mono text-sm">No projects in this category</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* GitHub CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="text-center mt-14"
        >
          <a
            href="https://github.com/atmanad"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary inline-flex items-center gap-2"
          >
            <FaGithub className="text-base" />
            <span>View all work on GitHub</span>
            <HiArrowRight className="text-sm" />
          </a>
        </motion.div>
      </div>

      {/* Divider line bottom */}
      <div className="absolute bottom-0 left-[10%] right-[10%] h-[1px] bg-gradient-to-r from-transparent via-white/5 to-transparent" />
    </section>
  );
}
